import React, { useContext, useEffect } from 'react';
import {
  SalvFieldHeader,
  SalvRadioToggleButton,
  SalvRadioToggleGroup,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import FormField from '../../../form/FormField';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import AlertStatusSetupContext, { GeneralSetupDataType } from './AlertStatusSetupContext';
import { Form as ReactForm } from 'react-bootstrap';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../../../organisation/feature/helper';
import { useMe } from '../../../organisation/useMe';

const validationSchema: Yup.Schema<GeneralSetupDataType> = Yup.object()
  .shape({
    name: Yup.string().required(),
    code: Yup.string()
      .matches(/^\w+$/i, {
        message: 'Code can only contain capital letters, numbers and underscores',
      })
      .required('Required'),
    isFinal: Yup.boolean().required(),
    isTruePositive: Yup.boolean().defined().nullable(),
    alertType: Yup.object().shape({
      isScreeningEnabled: Yup.boolean().required(),
      isMonitoringEnabled: Yup.boolean().required(),
      isBridgeEnabled: Yup.boolean().required(),
      isManualEnabled: Yup.boolean().required(),
    }),
    entityType: Yup.object().shape({
      isPerson: Yup.boolean().required(),
      isTransaction: Yup.boolean().required(),
    }),
  })
  .test({
    test: (values) => {
      if (
        values.alertType.isScreeningEnabled ||
        values.alertType.isMonitoringEnabled ||
        values.alertType.isBridgeEnabled ||
        values.alertType.isManualEnabled
      ) {
        return true;
      } else {
        return new Yup.ValidationError('At least one alert type has to be chosen', null, 'alertType');
      }
    },
  })
  .test({
    test: (values) => {
      if (values.entityType.isPerson || values.entityType.isTransaction) {
        return true;
      } else {
        return new Yup.ValidationError('At least one entity type has to be chosen', null, 'entityType');
      }
    },
  });

const GeneralSetup = () => {
  const { formRef, generalSetup, progressStepper, setAlertType, apiErrors } = useContext(AlertStatusSetupContext)!;

  const { data, setData, setValid, isSubmissionTouched, setSubmitted } = generalSetup;
  const { setGeneralSetupStep } = progressStepper;

  const { features } = useMe();
  const bridgeAlertsEnabled = isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features);

  const submit = (values: GeneralSetupDataType) => {
    setData(values);
    setSubmitted(true);
    setGeneralSetupStep({ type: 'filled' });
  };

  return (
    <div>
      <h2 className={'title-m m-b-12'}>General setup</h2>
      <Formik
        initialValues={data}
        validationSchema={validationSchema}
        onSubmit={submit}
        innerRef={formRef}
        enableReinitialize
      >
        {({ setFieldValue, setErrors, values, errors, isValid, dirty }: FormikProps<GeneralSetupDataType>) => {
          useEffect(() => {
            setValid(isValid);
          }, [isValid]);

          useEffect(() => setAlertType(values.alertType), [values.alertType]);

          useEffect(() => {
            if (dirty) {
              if (values.isFinal) {
                setFieldValue('isTruePositive', false);
              } else {
                setFieldValue('isTruePositive', null);
              }
            }
          }, [values.isFinal]);

          useEffect(() => {
            setErrors(apiErrors);
          }, [apiErrors]);

          return (
            <>
              <SalvTextField
                label={'Status name'}
                description={
                  'Status name is how it will be displayed across the system, appearing in various places like the alert view and status picker.'
                }
                placeholder={'Name for this status...'}
                className={'m-b-24'}
                value={values.name}
                onChange={(value) => {
                  setFieldValue('name', value);
                }}
                isInvalid={isSubmissionTouched && !!errors.name}
              />
              <div className={'m-b-24'}>
                <SalvTextField
                  label={'Status code'}
                  description={
                    <>
                      <div className={'m-b-4'}>
                        {`Status code is used by the system and must be unique. 
                        If you're using Decision Rules, ensure you've added all
                        necessary status codes there.`}
                      </div>
                      <div>{`Note: Code can only contain capital letters, numbers and underscores.`}</div>
                    </>
                  }
                  placeholder={'Unique code for this status...'}
                  value={values.code}
                  onChange={(value) => {
                    setFieldValue('code', value.toUpperCase());
                  }}
                  isInvalid={isSubmissionTouched && !!errors.code}
                />
                {isSubmissionTouched && !!errors.code && errors.code !== 'Required' && (
                  <ReactForm.Control.Feedback data-testid={`code`} type='invalid' className='d-block'>
                    {errors.code}
                  </ReactForm.Control.Feedback>
                )}
              </div>
              <SalvRadioToggleGroup
                label={'Final status'}
                description={
                  'When an alert reaches a final status, further changes are not expected. Final statuses typically resemble False or True positive, indicating a definitive outcome.'
                }
                value={values.isFinal}
                onChange={(value) => {
                  setFieldValue('isFinal', value === 'true');
                }}
                size={Size.MEDIUM}
                className={'m-b-24'}
              >
                <SalvRadioToggleButton value={false} label={'No'} />
                <SalvRadioToggleButton value={true} label={'Yes'} />
              </SalvRadioToggleGroup>
              {values.isFinal && (
                <SalvRadioToggleGroup
                  label={'True Positive'}
                  description={
                    'Mark this option if this status indicates true positive logic. The true positive logic is used in Screening checks for transactions and persons, and to trigger re-calculations of person Risk levels (if applicable).'
                  }
                  value={values.isTruePositive}
                  onChange={(value) => {
                    setFieldValue('isTruePositive', value === 'true');
                  }}
                  className={'m-b-24'}
                >
                  <SalvRadioToggleButton value={false} label={'No'} />
                  <SalvRadioToggleButton value={true} label={'Yes'} />
                </SalvRadioToggleGroup>
              )}
              <div className={'m-b-24'}>
                <SalvFieldHeader
                  label={'Enable for'}
                  description={
                    'Select the alert type for which you want this status to be enabled. You can set more detailed configurations in the upcoming steps.'
                  }
                />
                <div className={`list-box ${isSubmissionTouched && errors.alertType ? 'invalid' : ''}`}>
                  <div>Screening</div>
                  <FormField
                    id={'isScreeningEnabled'}
                    name={'isScreeningEnabled'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertType.isScreeningEnabled', e.target.checked);
                    }}
                    checked={values.alertType.isScreeningEnabled}
                    isInvalid={isSubmissionTouched && !!errors.alertType}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.alertType ? 'invalid' : ''}`}>
                  <div>Monitoring</div>
                  <FormField
                    id={'isMonitoringEnabled'}
                    name={'isMonitoringEnabled'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertType.isMonitoringEnabled', e.target.checked);
                    }}
                    checked={values.alertType.isMonitoringEnabled}
                    isInvalid={isSubmissionTouched && !!errors.alertType}
                  />
                </div>
                {bridgeAlertsEnabled && (
                  <div className={`list-box ${isSubmissionTouched && errors.alertType ? 'invalid' : ''}`}>
                    <div>Bridge</div>
                    <FormField
                      id={'isBridgeEnabled'}
                      name={'isBridgeEnabled'}
                      type='checkbox'
                      onFieldChange={(e) => {
                        setFieldValue('alertType.isBridgeEnabled', e.target.checked);
                      }}
                      checked={values.alertType.isBridgeEnabled}
                      isInvalid={isSubmissionTouched && !!errors.alertType}
                    />
                  </div>
                )}
                <div className={`list-box ${isSubmissionTouched && errors.alertType ? 'invalid' : ''}`}>
                  <div>Manual</div>
                  <FormField
                    id={'isManualEnabled'}
                    name={'isManualEnabled'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('alertType.isManualEnabled', e.target.checked);
                    }}
                    checked={values.alertType.isManualEnabled}
                    isInvalid={isSubmissionTouched && !!errors.alertType}
                  />
                </div>
              </div>
              <div>
                <SalvFieldHeader
                  label={'Entity type'}
                  description={'Choose alerts based on the entity type for which you want to enable this status.'}
                />
                <div className={`list-box ${isSubmissionTouched && errors.entityType ? 'invalid' : ''}`}>
                  <div>Transaction</div>
                  <FormField
                    id={'isTransaction'}
                    name={'isTransaction'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('entityType.isTransaction', e.target.checked);
                    }}
                    checked={values.entityType.isTransaction}
                    isInvalid={isSubmissionTouched && !!errors.entityType}
                  />
                </div>
                <div className={`list-box ${isSubmissionTouched && errors.entityType ? 'invalid' : ''}`}>
                  <div>Person</div>
                  <FormField
                    id={'isPerson'}
                    name={'isPerson'}
                    type='checkbox'
                    onFieldChange={(e) => {
                      setFieldValue('entityType.isPerson', e.target.checked);
                    }}
                    checked={values.entityType.isPerson}
                    isInvalid={isSubmissionTouched && !!errors.entityType}
                  />
                </div>
              </div>
            </>
          );
        }}
      </Formik>
    </div>
  );
};

export default GeneralSetup;
